import { z } from "zod";
import { Timestamp } from "./common.ts";

/**
 * Site-wide switches the admin controls at runtime (no deploy needed).
 *
 * Registration: when `registrationOpen` is false, OR when `maxNewAccountsPerHour` diver
 * accounts were already created in the last 60 minutes, a sign-up (auth.ts) for an email
 * WITHOUT an account creates nothing and sends no mail, yet answers exactly like any other
 * request (same 200 body). Existing accounts sign in normally. Such refusals are counted in
 * `refusedSignupsLastHour` so the admin sees bot pressure.
 *
 * Defaults on a fresh database: closed, 10 per hour. Production opens registration only once
 * an email service is configured.
 */
export const SiteSettings = z
  .object({
    registrationOpen: z.boolean(),
    /**
     * Global cap on NEW diver accounts per rolling hour (all IPs together). 0 = no new accounts
     * at all (as strict as closed). Enforced atomically in the database, so bursts cannot exceed it.
     */
    maxNewAccountsPerHour: z.number().int().min(0).max(1000),
    /** Read-only: refused sign-ups (closed or over cap) in the last 60 minutes. */
    refusedSignupsLastHour: z.number().int().min(0),
    /** Read-only: diver accounts created in the last 60 minutes. */
    newAccountsLastHour: z.number().int().min(0),
    /** Read-only: whether an email transport is configured (confirmation and reset mails can be delivered). */
    mailConfigured: z.boolean(),
    updatedAt: Timestamp.nullable(),
  })
  .strict();
export type SiteSettings = z.infer<typeof SiteSettings>;

/** PATCH /api/admin/settings (admin). Opening registration while `mailConfigured` is false → 409. */
export const SiteSettingsUpdate = z
  .object({
    registrationOpen: z.boolean(),
    maxNewAccountsPerHour: z.number().int().min(0).max(1000),
  })
  .partial()
  .strict();
export type SiteSettingsUpdate = z.infer<typeof SiteSettingsUpdate>;

/**
 * GET /api/config (public). Lets the login page say "registration closed" up front. It is
 * true only if registration is open AND mail is configured; it never reflects the hourly cap
 * (that would leak bot-defence state).
 */
export const PublicConfig = z.object({ registrationOpen: z.boolean() }).strict();
export type PublicConfig = z.infer<typeof PublicConfig>;
